import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const isPdfFile = (file) =>
  Boolean(file) &&
  (file.type === 'application/pdf' ||
    file.name?.toLowerCase().endsWith('.pdf'));

export const CertificateManualUpload = ({
  students = [],
  uploadUrl,
  loadingStudents = false,
  loadError = '',
  onUploaded,
}) => {
  const [file, setFile] = useState(null);
  const [studentNumber, setStudentNumber] = useState('');
  const [result, setResult] = useState(null);
  const [uploadState, setUploadState] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const fileInput = useRef(null);
  const navigate = useNavigate();
  const uploading = uploadState === 'subiendo';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);

    const validStudent = students.some(
      (student) => student.matricula === studentNumber,
    );
    if (!validStudent || !isPdfFile(file)) {
      setResult({
        type: 'warning',
        message: 'Selecciona un alumno válido y un archivo PDF.',
      });
      return;
    }

    setUploadState('subiendo');
    setUploadMessage('Subiendo la constancia...');
    setProgress(0);
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('matricula_alumno', studentNumber);

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const nextProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setProgress(nextProgress);
          setUploadMessage(
            nextProgress === 100
              ? 'Archivo enviado. Procesando en el servidor...'
              : 'Subiendo la constancia...',
          );
        },
      });

      if (response.data?.success === false) {
        setUploadState('error');
        setUploadMessage(
          response.data.message || 'No se pudo cargar la constancia.',
        );
        return;
      }

      setUploadState('exito');
      setUploadMessage(
        response.data?.message || 'La constancia se cargó correctamente.',
      );
      setFile(null);
      setStudentNumber('');
      if (fileInput.current) fileInput.current.value = '';
      if (typeof onUploaded === 'function') {
        onUploaded(response.data);
      }
    } catch (error) {
      setUploadState('error');
      setUploadMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'No se pudo cargar la constancia.',
      );
    }
  };

  const closeResult = () => {
    if (!uploading) {
      setUploadState(null);
      setUploadMessage('');
    }
  };

  return (
    <>
      <section
        className="certificate-manual-upload"
        aria-labelledby="certificate-manual-upload-title"
      >
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Carga manual existente</p>
          <h2 id="certificate-manual-upload-title">
            Subir constancia existente
          </h2>
          <p>
            Selecciona al alumno y carga una constancia PDF ya generada.
          </p>
        </header>

        <form className="admin-operation-panel" onSubmit={handleSubmit}>
          <div className="admin-operation-grid">
            <section className="admin-operation-card">
              <div className="admin-operation-card-heading">
                <span>1</span>
                <div>
                  <h2>Selecciona al alumno</h2>
                  <p>Busca por matrícula o nombre.</p>
                </div>
              </div>
              <div className="admin-operation-field">
                <label htmlFor="certificate-student">
                  Alumno para carga manual
                </label>
                <input
                  id="certificate-student"
                  type="text"
                  list="certificate-students"
                  value={studentNumber}
                  onChange={(event) => setStudentNumber(event.target.value)}
                  placeholder={
                    loadingStudents
                      ? 'Cargando alumnos...'
                      : 'Escribe una matrícula'
                  }
                  disabled={loadingStudents || uploading}
                  autoComplete="off"
                  required
                />
                <datalist id="certificate-students">
                  {students.map((student) => (
                    <option
                      key={student.id ?? student.matricula}
                      value={student.matricula}
                    >
                      {[student.nombre, student.apellido]
                        .filter(Boolean)
                        .join(' ')}
                    </option>
                  ))}
                </datalist>
              </div>
            </section>

            <section className="admin-operation-card">
              <div className="admin-operation-card-heading">
                <span>2</span>
                <div>
                  <h2>Selecciona el PDF</h2>
                  <p>La constancia debe estar en formato .pdf.</p>
                </div>
              </div>
              <div className="admin-operation-field">
                <label htmlFor="certificate-file">Archivo de constancia</label>
                <input
                  ref={fileInput}
                  className="admin-operation-file"
                  id="certificate-file"
                  type="file"
                  onChange={(event) =>
                    setFile(event.target.files[0] || null)
                  }
                  accept=".pdf,application/pdf"
                  disabled={uploading}
                  required
                />
                {file && <small>Archivo seleccionado: {file.name}</small>}
              </div>
            </section>
          </div>

          {(loadError || result) && (
            <div
              className={
                'admin-operation-status admin-operation-status-' +
                (result?.type || 'error')
              }
              role="status"
            >
              {result?.message || loadError}
            </div>
          )}

          <div className="admin-operation-actions">
            <button
              className="admin-operation-button"
              type="submit"
              disabled={loadingStudents || students.length === 0 || uploading}
            >
              {uploading ? 'Subiendo constancia...' : 'Subir constancia'}
            </button>
            <button
              className="admin-operation-button admin-operation-button-secondary"
              type="button"
              onClick={() => navigate('/admin-home')}
              disabled={uploading}
            >
              Regresar al inicio
            </button>
          </div>
        </form>
      </section>

      {uploadState && (
        <div className="admin-operation-modal-overlay" role="presentation">
          <div
            className="admin-operation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="certificate-upload-title"
          >
            <h2 id="certificate-upload-title">
              {uploadState === 'subiendo' && 'Carga en proceso'}
              {uploadState === 'exito' && 'Constancia cargada'}
              {uploadState === 'error' && 'No se pudo completar la carga'}
            </h2>
            <p>{uploadMessage}</p>

            {uploading && (
              <>
                <div className="admin-operation-progress">
                  <div style={{ width: progress + '%' }} />
                </div>
                <p>
                  {progress > 0
                    ? progress + '% enviado'
                    : 'Preparando archivo...'}
                </p>
              </>
            )}

            {!uploading && (
              <button
                className="admin-operation-button"
                type="button"
                onClick={closeResult}
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
